import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { deleteDoc, doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import PageShell from "../layout/PageShell";

const TIME_DOC = doc(db, "systemConfig", "permanentTime");

const PermanentTimeSettings: React.FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");
  const [letter, setLetter] = useState("N");
  const [confirmAction, setConfirmAction] = useState<"save" | "remove" | null>(null);
  const [confirmWord, setConfirmWord] = useState("");

  useEffect(() => {
    let cancelled = false;
    getDoc(TIME_DOC)
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const data = snap.data();
        setEffectiveDate(typeof data.effectiveDate === "string" ? data.effectiveDate : "");
        setLetter(data.letter === "A" || data.letter === "N" || data.letter === "" ? data.letter : "N");
      })
      .catch(() => {
        toast({ title: "Could not load permanent time", status: "error", duration: 3000 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const closeConfirm = () => {
    if (saving) return;
    setConfirmAction(null);
    setConfirmWord("");
  };

  const openConfirm = (action: "save" | "remove") => {
    if (action === "save" && !/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
      toast({ title: "Choose the date this takes effect.", status: "error", duration: 3000 });
      return;
    }
    setConfirmWord("");
    setConfirmAction(action);
  };

  const applyConfirm = async () => {
    if (confirmAction === "save" && confirmWord.trim() !== "save") return;
    if (confirmAction === "remove" && confirmWord.trim() !== "remove") return;
    setSaving(true);
    try {
      if (confirmAction === "save") {
        await setDoc(TIME_DOC, {
          effectiveDate,
          letter,
          updatedAt: serverTimestamp(),
        });
        toast({ title: "Permanent time saved", status: "success", duration: 2500 });
      } else {
        await deleteDoc(TIME_DOC);
        setEffectiveDate("");
        setLetter("N");
        toast({ title: "Permanent time removed", status: "success", duration: 2500 });
      }
      setConfirmAction(null);
      setConfirmWord("");
    } catch {
      toast({ title: "Could not update permanent time", status: "error", duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const wordMatches = confirmAction !== null && confirmWord.trim() === confirmAction;
  const eastern = `-05:00${letter}`;
  const atlantic = `-04:00${letter}`;
  const ending = letter ? `The ending is ${letter}.` : "There is no ending.";

  return (
    <PageShell
      title="Permanent time"
      description="From the effective date, every store sends the same ending. Eastern keeps -05:00. Atlantic keeps -04:00."
    >
      {loading ? <Spinner /> : (
        <Box bg="white" border="1px solid" borderColor="#d5e4ea" borderRadius="lg" p={5} maxW="520px">
          <FormControl mb={4}>
            <FormLabel>Effective date</FormLabel>
            <Input type="date" value={effectiveDate} onChange={(event) => setEffectiveDate(event.target.value)} />
          </FormControl>
          <FormControl mb={4}>
            <FormLabel>Ending</FormLabel>
            <Select value={letter} onChange={(event) => setLetter(event.target.value)}>
              <option value="">No ending</option>
              <option value="N">N, standard time</option>
              <option value="A">A, daylight time</option>
            </Select>
          </FormControl>
          <Text fontSize="sm" color="gray.600" mb={4}>
            Eastern sends {eastern}. Atlantic sends {atlantic}. {ending} This applies to every store on or after the effective date.
          </Text>
          <Button colorScheme="blue" mr={3} onClick={() => openConfirm("save")} isDisabled={saving}>Save</Button>
          <Button variant="outline" colorScheme="red" onClick={() => openConfirm("remove")} isDisabled={saving}>Remove</Button>
        </Box>
      )}
      <Modal isOpen={confirmAction !== null} onClose={closeConfirm} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{confirmAction === "remove" ? "Remove permanent time" : "Save permanent time"}</ModalHeader>
          <ModalBody>
            <Text fontSize="sm" mb={4}>
              {confirmAction === "remove"
                ? "Every store returns to the Sunday clock-change rule. Type the confirmation word to remove this setting."
                : `From ${effectiveDate}, Eastern sends ${eastern}. Atlantic sends ${atlantic}. ${ending} Type the confirmation word to save.`}
            </Text>
            <FormControl isRequired>
              <FormLabel>{confirmAction === "remove" ? 'Type "remove" to remove' : 'Type "save" to save'}</FormLabel>
              <Input
                value={confirmWord}
                onChange={(event) => setConfirmWord(event.target.value)}
                autoComplete="off"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="outline" mr={3} onClick={closeConfirm} isDisabled={saving}>Cancel</Button>
            <Button
              colorScheme={confirmAction === "remove" ? "red" : "blue"}
              isDisabled={!wordMatches}
              isLoading={saving}
              onClick={applyConfirm}
            >
              {confirmAction === "remove" ? "Remove" : "Save"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
};

export default PermanentTimeSettings;
